/**
 * Inline hover magnifier for product media images (desktop/hover only).
 * Scales the image and updates transform origin based on pointer position.
 */
const hoverMediaQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

/**
 * Determine whether magnifier should run (desktop hover + fine pointer).
 * @returns {boolean}
 */
const shouldEnableMagnifier = () => hoverMediaQuery.matches;

const bindings = new WeakMap();

const cleanupTarget = (container) => {
  const existing = bindings.get(container);
  if (existing) {
    container.removeEventListener('pointerenter', existing.startMagnify);
    container.removeEventListener('pointermove', existing.handleMove);
    container.removeEventListener('pointerleave', existing.stopMagnify);
    bindings.delete(container);
  }
  container.removeAttribute('data-magnifier-bound');
};

const initMagnifier = () => {
  if (!shouldEnableMagnifier()) return;

  const targets = document.querySelectorAll('[data-magnifier="true"]');
  targets.forEach((container) => {
    cleanupTarget(container);
    setupTarget(container);
  });
};

/**
 * Bind magnifier behavior to a single media container.
 * @param {HTMLElement} container
 */
const setupTarget = (container) => {
  const img = container.querySelector('.product-media__image');
  if (!img) return;

  container.dataset.magnifierBound = 'true';

  const scale = Number(container.dataset.magnifierScale || 2.5);
  let frame;

  const updateOrigin = (event) => {
    const rect = container.getBoundingClientRect();
    const x = ((event.clientX - rect.left) / rect.width) * 100;
    const y = ((event.clientY - rect.top) / rect.height) * 100;
    img.style.transformOrigin = `${x}% ${y}%`;
  };

  const startMagnify = (event) => {
    if (!shouldEnableMagnifier()) return;
    container.classList.add('is-magnifying');
    img.style.transform = `scale(${scale})`;
    updateOrigin(event);
  };

  const stopMagnify = () => {
    container.classList.remove('is-magnifying');
    img.style.transform = '';
    img.style.transformOrigin = '';
  };

  const handleMove = (event) => {
    if (!container.classList.contains('is-magnifying')) return;
    if (frame) cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => updateOrigin(event));
  };

  container.addEventListener('pointerenter', startMagnify);
  container.addEventListener('pointermove', handleMove);
  container.addEventListener('pointerleave', stopMagnify);

  bindings.set(container, { startMagnify, handleMove, stopMagnify });
};

initMagnifier();
document.addEventListener('DOMContentLoaded', initMagnifier);
document.addEventListener('shopify:section:load', initMagnifier);
document.addEventListener('variant:update', () => {
  // Re-init after variant-driven gallery replacement.
  requestAnimationFrame(initMagnifier);
});
document.addEventListener('variant:selected', () => {
  requestAnimationFrame(initMagnifier);
});
if (hoverMediaQuery.addEventListener) {
  hoverMediaQuery.addEventListener('change', initMagnifier);
} else if (hoverMediaQuery.addListener) {
  hoverMediaQuery.addListener(initMagnifier);
}

